import { z } from 'zod';

const optionalNumber = (min?: number) =>
  z.preprocess((value) => {
    if (value === '' || value === null || typeof value === 'undefined') {
      return undefined;
    }
    if (typeof value === 'number' && Number.isNaN(value)) {
      return undefined;
    }
    return value;
  }, min === undefined ? z.coerce.number().optional() : z.coerce.number().min(min).optional());

// Lead form validation schema - SINGLE SOURCE OF TRUTH
export const leadFormSchema = z.object({
  // Customer
  customer_id: z.string().min(1, 'Customer is required'),
  has_co_buyer: z.boolean().optional(),
  co_buyer_customer_id: z.string().optional(),
  
  // Lead Details
  title: z.string().min(1, 'Title is required').max(255),
  description: z.string().optional(),
  lead_source: z.string().optional(),
  // #562 — set when the deal is created from an enquiry; hidden, never a form field.
  source_enquiry_id: z.string().optional(),
  location_id: z.string().min(1, 'Location is required'),
  // Removed: lead_status - now using stage-based workflow
  
  // Pipeline (strings for ULID)
  pipeline_id: z.string().min(1, 'Pipeline is required'),
  stage_id: z.string().min(1, 'Stage is required'),
  
  // Boat Interest (string ULID - inventory uses HasUlid)
  boat_id: z.string().optional(),
  // #706 — brand interest (canonical manufacturer ULID). Nullable, not just
  // optional: explicit null is how the update path clears the brand (same
  // convention as assigned_to).
  manufacturer_id: z.string().nullable().optional(),
  boat_type_interest: z.string().max(100).optional(),
  
  // Budget
  budget_min: optionalNumber(0),
  budget_max: optionalNumber(0),
  
  // Financial
  estimated_value: optionalNumber(0),
  expected_close_date: z.string().optional(),
  
  // Assignment (string for employee ULID)
  // #733 — nullable, not just optional: an explicit null is how the update
  // path clears assignment and returns a lead to the unassigned queue.
  assigned_to: z.string().nullable().optional(),
  next_follow_up_date: z.string().optional(),
  
  // Lead score (optional - defaults to 50 in form)
  score: z.number().int().min(0).max(100).optional(),

  // Trade-in fields (optional - defaults to false in form)
  has_trade_in: z.boolean().optional(),
  tradein_year: optionalNumber(),
  tradein_make: z.string().optional(),
  tradein_model: z.string().optional(),
  tradein_length: optionalNumber(0),
  tradein_condition: z.string().optional(),
  tradein_hin: z.string().optional(),
  tradein_hours: optionalNumber(0),
  tradein_estimated_value: optionalNumber(0),
  tradein_notes: z.string().optional(),
}).refine(
  (data) => {
    // Budget max should be greater than or equal to budget min
    if (data.budget_min !== undefined && data.budget_max !== undefined) {
      return data.budget_max >= data.budget_min;
    }
    return true;
  },
  {
    message: 'Budget maximum must be greater than or equal to budget minimum',
    path: ['budget_max'],
  }
)
  .refine(
    (data) => {
      if (data.has_co_buyer) {
        return !!data.co_buyer_customer_id;
      }
      return true;
    },
    {
      message: 'Co-buyer is required when enabled',
      path: ['co_buyer_customer_id'],
    }
  )
  .refine(
    (data) => {
      if (data.has_trade_in) {
        return data.tradein_year !== undefined;
      }
      return true;
    },
    {
      message: 'Trade-in year is required when trade-in is enabled',
      path: ['tradein_year'],
    }
  )
  .refine(
    (data) => {
      if (data.has_trade_in) {
        return !!data.tradein_make;
      }
      return true;
    },
    {
      message: 'Trade-in make is required when trade-in is enabled',
      path: ['tradein_make'],
    }
  )
  .refine(
    (data) => {
      if (data.has_trade_in) {
        return !!data.tradein_model;
      }
      return true;
    },
    {
      message: 'Trade-in model is required when trade-in is enabled',
      path: ['tradein_model'],
    }
  )
  .refine(
    (data) => {
      if (data.has_co_buyer && data.co_buyer_customer_id) {
        return data.co_buyer_customer_id !== data.customer_id;
      }
      return true;
    },
    {
      message: 'Co-buyer must be different from the customer',
      path: ['co_buyer_customer_id'],
    }
  );

// Export as LeadFormData - SINGLE SOURCE OF TRUTH for lead form validation and types
export type LeadFormData = z.infer<typeof leadFormSchema>;

// Legacy export for backward compatibility (deprecated)
export type LeadFormSchemaType = LeadFormData;
